import { Button, List, Modal, Typography, message } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useRequest from '../hooks/useRequest';
import { useCashDrawer } from '../contexts/CashDrawerContext';

const { Text } = Typography;

interface PendingTransfer {
    id: number;
    value: number;
    stamp: string | null;
    observations: string | null;
    fromCashPointId: number | null;
    fromCashPointName: string | null;
    senderUserId: number | null;
    senderUserName: string | null;
}

const POLL_INTERVAL_MS = 30000;

/**
 * Watches for incoming cash transfers awaiting acceptance at the current register
 * and prompts the user to accept them (Finding 1). Unaccepted transfers stay pending
 * server-side, so they reappear on the next login for this cashpoint.
 */
const PendingTransfersModal = () => {
    const { t } = useTranslation();
    const request = useRequest();
    const { cashPointId, refreshBalance } = useCashDrawer();
    const [transfers, setTransfers] = useState<PendingTransfer[]>([]);
    const [open, setOpen] = useState(false);
    const [acceptingId, setAcceptingId] = useState<number | null>(null);
    // Suppresses auto-reopen after the user dismisses, until the pending set changes.
    const dismissedSignatureRef = useRef<string | null>(null);

    const fetchPending = useCallback(async () => {
        if (!cashPointId) {
            setTransfers([]);
            setOpen(false);
            return;
        }
        try {
            const data = await request<PendingTransfer[]>({
                path: `/cash-drawer/pending-transfers/${cashPointId}`,
            });
            const pending = Array.isArray(data) ? data : [];
            setTransfers(pending);

            const signature = pending
                .map(transfer => transfer.id)
                .sort((a, b) => a - b)
                .join(',');
            if (pending.length === 0) {
                dismissedSignatureRef.current = null;
                setOpen(false);
            } else if (signature !== dismissedSignatureRef.current) {
                // A new/different set of pending transfers -> prompt again.
                dismissedSignatureRef.current = null;
                setOpen(true);
            }
        } catch (error) {
            console.error('Failed to load pending transfers:', error);
        }
    }, [cashPointId, request]);

    // Reset dismissal when the register changes (e.g. a fresh login) and fetch.
    useEffect(() => {
        dismissedSignatureRef.current = null;
        fetchPending();
    }, [fetchPending]);

    useEffect(() => {
        if (!cashPointId) return undefined;
        const interval = setInterval(fetchPending, POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [cashPointId, fetchPending]);

    const handleAccept = useCallback(
        async (transfer: PendingTransfer) => {
            if (!cashPointId) return;
            setAcceptingId(transfer.id);
            try {
                await request<{ success: boolean }>({
                    path: `/cash-drawer/transfers/${transfer.id}/accept`,
                    method: 'POST',
                    params: { cashPointId },
                });
                message.success(t('cashDrawer.pendingTransfers.accepted'));
                await refreshBalance();
                await fetchPending();
            } catch (error) {
                message.error(
                    error instanceof Error
                        ? error.message
                        : t('cashDrawer.pendingTransfers.acceptError'),
                );
            } finally {
                setAcceptingId(null);
            }
        },
        [cashPointId, request, refreshBalance, fetchPending, t],
    );

    const handleDismiss = useCallback(() => {
        dismissedSignatureRef.current = transfers
            .map(transfer => transfer.id)
            .sort((a, b) => a - b)
            .join(',');
        setOpen(false);
    }, [transfers]);

    if (transfers.length === 0) return null;

    return (
        <Modal
            open={open}
            title={t('cashDrawer.pendingTransfers.title')}
            onCancel={handleDismiss}
            footer={
                <Button onClick={handleDismiss}>{t('cashDrawer.pendingTransfers.later')}</Button>
            }
            maskClosable={false}
        >
            <Text type="secondary">{t('cashDrawer.pendingTransfers.description')}</Text>
            <List
                style={{ marginTop: 12 }}
                dataSource={transfers}
                rowKey={transfer => transfer.id}
                renderItem={transfer => (
                    <List.Item
                        actions={[
                            <Button
                                key="accept"
                                type="primary"
                                loading={acceptingId === transfer.id}
                                onClick={() => handleAccept(transfer)}
                            >
                                {t('cashDrawer.pendingTransfers.accept')}
                            </Button>,
                        ]}
                    >
                        <List.Item.Meta
                            title={`${Math.round(transfer.value)} RON`}
                            description={t('cashDrawer.pendingTransfers.fromLine', {
                                cashPoint:
                                    transfer.fromCashPointName ??
                                    String(transfer.fromCashPointId ?? '?'),
                                user: transfer.senderUserName ?? String(transfer.senderUserId ?? '?'),
                            })}
                        />
                        {transfer.observations ? (
                            <Text type="secondary">{transfer.observations}</Text>
                        ) : null}
                    </List.Item>
                )}
            />
        </Modal>
    );
};

export default PendingTransfersModal;
